import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapPin, Users } from 'lucide-react';
import { mockData } from '../mock';

const About = () => {
  const { organization } = mockData;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-mauve-100 to-white py-16 pb-4 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-darkslate-700">
            About Us
          </h1>
        </div>
      </section>

      {/* About Content */}
      <section className="py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-mauve-200" data-testid="about-content-card">
            <CardContent className="space-y-5 text-darkslate-600 leading-relaxed pt-8">
              <p>Kibbles & Bits of Kindness was founded on the belief that pets are family, and no one should ever have to choose between keeping their beloved companion and meeting their basic needs. We understand that life can be unpredictable. Job loss, illness, rising costs, unexpected vet bills, or personal hardship can quickly put families in impossible positions. During those moments, a little support can mean everything.</p>

              <p>We are a grassroots, community-driven nonprofit dedicated to keeping animals with the people who love them. Our mission is to prevent unnecessary surrenders, and prevent abandonment, by providing pet food, basic supplies, and connection to supportive resources for individuals and families facing financial hardship.</p>

              <p>Since opening our doors, we have supported dogs, cats, and even bunnies through emergency assistance. What began as urgent, short-term help has grown into a vision for consistent, reliable support, ensuring that members of our community can access care before a crisis becomes a surrender, particularly at a time when shelters and rescues are already at max capacity and barely hanging on.</p>

              <p>In addition to supporting families, we also work alongside compassionate community members in rural and remote areas who care for stray and abandoned animals. In places where formal rescue services may not exist, local residents often step in to feed, shelter, and protect these animals. When possible, we help provide food and basic supplies so those animals can be cared for safely until longer-term solutions can be found.</p>

              <p>At Kibbles & Bits of Kindness, we believe prevention is powerful. When families are supported early, animals stay home. Stress is reduced. Shelters are relieved, and most importantly, the human-animal bond can remain unbroken.</p>

              <p>We are powered by compassion, generosity, and community collaboration. Every bag of food donated, every dollar contributed, and every act of kindness helps stabilize a household and protect a cherished relationship.</p>

              <p>Behind every request for help is a family doing their best, and a pet who depends on them.</p>

              <p>This is more than a pet food pantry. It is a bridge during hard times. It is dignity without judgment. It is neighbors helping neighbors.</p>

              <p className="font-semibold text-darkslate-700">Together, we are building a community where no one has to face hardship alone, and where love for our animals is never out of reach.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Location & Community */}
      <section className="py-12 px-4 bg-mauve-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-mauve-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-olive-100 rounded-lg flex items-center justify-center mb-3">
                  <MapPin className="h-6 w-6 text-olive-600" />
                </div>
                <CardTitle className="text-xl text-darkslate-700">Our Location</CardTitle>
              </CardHeader>
              <CardContent className="text-darkslate-500">
                {mockData.aboutUs.location}
              </CardContent>
            </Card>

            <Card className="border-2 border-mauve-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                  <Users className="h-6 w-6 text-amber-600" />
                </div>
                <CardTitle className="text-xl text-darkslate-700">Our Community</CardTitle>
              </CardHeader>
              <CardContent className="text-darkslate-500">
                We serve {organization.serviceArea}, helping local families keep their beloved pets safe, healthy, and happy at home where they belong.
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
